import { useState } from 'react';
import type { AtualizarContaContabilDto, ContaContabilResponse, CriarContaContabilDto } from '@triade/contracts';
import { useAtualizarContaContabil, useCriarContaContabil, useExcluirContaContabil } from '@/api/itens-cif';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CodigosExternos } from '@/components/externos';
import { ApiError } from '@/lib/api';
import { contaVazia, deConta, montarCorpoConta, type ContaFormEntrada, type ErroCampoForm } from './item-form';

const CAMPO = 'h-[34px] w-full rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';
const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';

const mensagemApi = (e: unknown) => (e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');

/** Conta contabil: codigo, descricao e ativo. Codigos externos so depois de criada (entidade 'contas-contabeis'). */
export function ContaContabilDialog({ conta, podeEditar, onFechar }: { conta: ContaContabilResponse | null; podeEditar: boolean; onFechar: () => void }) {
  const criar = useCriarContaContabil();
  const atualizar = useAtualizarContaContabil();
  const excluir = useExcluirContaContabil();
  const [valor, setValor] = useState<ContaFormEntrada>(() => (conta ? deConta(conta) : contaVazia()));
  const [erroCampo, setErroCampo] = useState<ErroCampoForm | null>(null);
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const salvando = criar.isPending || atualizar.isPending || excluir.isPending;

  async function gravar() {
    setErroGeral(null);
    setErroCampo(null);
    const montado = montarCorpoConta(valor, conta === null);
    if (!montado.ok) return setErroCampo(montado.erro);
    try {
      if (conta) await atualizar.mutateAsync({ id: conta.id, dto: montado.corpo as AtualizarContaContabilDto });
      else await criar.mutateAsync(montado.corpo as CriarContaContabilDto);
      onFechar();
    } catch (e) {
      setErroGeral(mensagemApi(e));
    }
  }

  /** Exclusao so sem uso: a API responde 409 CONTA_EM_USO (mostramos a mensagem dela). */
  async function excluirConta() {
    if (!conta) return;
    setErroGeral(null);
    try {
      await excluir.mutateAsync(conta.id);
      onFechar();
    } catch (e) {
      setConfirmandoExclusao(false);
      setErroGeral(e instanceof ApiError ? e.message : 'falha de comunicação com a API');
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !salvando && onFechar()}>
      <DialogContent className="max-w-[640px]">
        <DialogHeader>
          <DialogTitle>{conta ? 'Conta contábil' : 'Nova conta contábil'}</DialogTitle>
          <DialogDescription>Conta do plano do cliente. Os itens de CIF vinculam contas para lançar os valores.</DialogDescription>
        </DialogHeader>

        <fieldset disabled={!podeEditar || salvando} className="flex flex-col gap-4">
          <div>
            <label htmlFor="conta-codigo" className={ROTULO}>Código</label>
            <input id="conta-codigo" maxLength={40} className={CAMPO} value={valor.codigo} aria-invalid={erroCampo?.campo === 'codigo' ? true : undefined} onChange={(e) => setValor((v) => ({ ...v, codigo: e.target.value }))} />
            {erroCampo?.campo === 'codigo' && <p role="alert" className="mt-1 text-caption text-danger-ink">{erroCampo.mensagem}</p>}
          </div>
          <div>
            <label htmlFor="conta-descricao" className={ROTULO}>Descrição</label>
            <input id="conta-descricao" maxLength={200} className={CAMPO} value={valor.descricao} aria-invalid={erroCampo?.campo === 'descricao' ? true : undefined} onChange={(e) => setValor((v) => ({ ...v, descricao: e.target.value }))} />
            {erroCampo?.campo === 'descricao' && <p role="alert" className="mt-1 text-caption text-danger-ink">{erroCampo.mensagem}</p>}
          </div>
          {conta && (
            <label className="flex items-center gap-2 text-small text-ink-body">
              <input type="checkbox" checked={valor.ativo} onChange={(e) => setValor((v) => ({ ...v, ativo: e.target.checked }))} />
              Ativa
            </label>
          )}
        </fieldset>

        {conta && (
          <div className="border-t border-border pt-4">
            <CodigosExternos entidade="contas-contabeis" id={conta.id} editavel={podeEditar} />
          </div>
        )}

        {erroGeral && <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-small text-danger-ink">{erroGeral}</p>}

        {conta && podeEditar && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
            {confirmandoExclusao ? (
              <>
                <span className="text-small text-ink-body">Excluir a conta {conta.codigo}? Só é possível se ela não estiver em uso.</span>
                <span className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setConfirmandoExclusao(false)} disabled={salvando}>Não excluir</Button>
                  <Button variant="destructive" size="sm" onClick={() => void excluirConta()} disabled={salvando}>
                    {excluir.isPending ? 'Excluindo…' : 'Confirmar exclusão'}
                  </Button>
                </span>
              </>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setConfirmandoExclusao(true)} disabled={salvando}>
                Excluir conta
              </Button>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onFechar} disabled={salvando}>
            {podeEditar ? 'Cancelar' : 'Fechar'}
          </Button>
          {podeEditar && (
            <Button onClick={() => void gravar()} disabled={salvando}>
              {salvando ? 'Gravando…' : 'Gravar'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
